import { diff_match_patch, DIFF_DELETE, DIFF_INSERT } from 'diff-match-patch'
import type { DialogueLine, DiffSegment, ScriptSuggestionChange } from '../../../types/script-refinement'

const dmp = new diff_match_patch()

type InternalDialogue = DialogueLine & { index: number }

const normalize = (value: string) => value.replace(/\s+/g, ' ').trim()

const computeDiffSegments = (originalText: string, updatedText: string): DiffSegment[] => {
  const diffs = dmp.diff_main(originalText, updatedText) as Array<[number, string]>
  dmp.diff_cleanupSemantic(diffs)
  return diffs
    .filter(([, text]) => text.length > 0)
    .map(([op, text]): DiffSegment => {
      if (op === DIFF_INSERT) return { type: 'insert', value: text }
      if (op === DIFF_DELETE) return { type: 'delete', value: text }
      return { type: 'equal', value: text }
    })
}

const findNextExistingId = (proposed: DialogueLine[], startIndex: number): string | null => {
  for (let i = startIndex; i < proposed.length; i += 1) {
    const candidate = proposed[i]
    if (candidate.lineId) {
      return candidate.lineId
    }
  }
  return null
}

export const buildScriptSuggestionChanges = (original: DialogueLine[], proposed: DialogueLine[]): ScriptSuggestionChange[] => {
  const originalIndexed: InternalDialogue[] = original.map((line, index) => ({ ...line, index }))
  const originalMap = new Map<string, InternalDialogue>()
  originalIndexed.forEach((entry) => {
    if (entry.lineId) {
      originalMap.set(entry.lineId, entry)
    }
  })

  const usedOriginalIds = new Set<string>()
  const changes: ScriptSuggestionChange[] = []
  let lastExistingLineId: string | null = null

  const resolveBeforeId = (currentIndex: number) => findNextExistingId(proposed, currentIndex + 1)

  proposed.forEach((line, index) => {
    const beforeId = resolveBeforeId(index)
    if (line.lineId && originalMap.has(line.lineId)) {
      const originalEntry = originalMap.get(line.lineId)!
      usedOriginalIds.add(line.lineId)
      lastExistingLineId = line.lineId
      const textChanged = normalize(line.line) !== normalize(originalEntry.line)
      const speakerChanged = (line.speaker || 'A') !== (originalEntry.speaker || 'A')
      if (textChanged || speakerChanged) {
        changes.push({
          id: crypto.randomUUID(),
          kind: 'modify',
          targetLineId: line.lineId,
          originalText: originalEntry.line,
          proposedText: line.line,
          originalSpeaker: originalEntry.speaker,
          proposedSpeaker: line.speaker,
          diff: computeDiffSegments(originalEntry.line, line.line),
          targetIndex: index,
          afterLineId: lastExistingLineId,
          beforeLineId: beforeId,
        })
      }
    } else {
      changes.push({
        id: crypto.randomUUID(),
        kind: 'insert',
        afterLineId: lastExistingLineId,
        beforeLineId: beforeId,
        proposedText: line.line,
        proposedSpeaker: line.speaker,
        diff: computeDiffSegments('', line.line),
        targetIndex: index,
      })
    }
  })

  originalIndexed.forEach((entry) => {
    if (entry.lineId && !usedOriginalIds.has(entry.lineId)) {
      changes.push({
        id: crypto.randomUUID(),
        kind: 'delete',
        targetLineId: entry.lineId,
        originalText: entry.line,
        originalSpeaker: entry.speaker,
        diff: computeDiffSegments(entry.line, ''),
        targetIndex: entry.index,
      })
    }
  })

  return changes.sort((a, b) => a.targetIndex - b.targetIndex)
}

export const applySuggestionToScript = (current: DialogueLine[], suggestion: ScriptSuggestionChange): DialogueLine[] => {
  const next = current.map((line) => ({ ...line }))

  switch (suggestion.kind) {
    case 'modify': {
      const idx = next.findIndex((line) => line.lineId === suggestion.targetLineId)
      if (idx === -1) throw new Error('Target line not found for modification')
      next[idx] = {
        lineId: next[idx].lineId,
        speaker: suggestion.proposedSpeaker ?? next[idx].speaker,
        line: suggestion.proposedText ?? next[idx].line,
      }
      return next
    }
    case 'delete': {
      const filtered = next.filter((line) => line.lineId !== suggestion.targetLineId)
      if (filtered.length === next.length) {
        throw new Error('Target line not found for deletion')
      }
      return filtered
    }
    case 'insert': {
      const insertLine: DialogueLine = {
        lineId: null,
        speaker: suggestion.proposedSpeaker ?? 'A',
        line: suggestion.proposedText ?? '',
      }
      let insertIndex = next.length
      if (suggestion.afterLineId) {
        const afterIdx = next.findIndex((line) => line.lineId === suggestion.afterLineId)
        if (afterIdx !== -1) {
          insertIndex = afterIdx + 1
        } else if (suggestion.beforeLineId) {
          const beforeIdx = next.findIndex((line) => line.lineId === suggestion.beforeLineId)
          insertIndex = beforeIdx === -1 ? insertIndex : beforeIdx
        }
      } else if (suggestion.beforeLineId) {
        const beforeIdx = next.findIndex((line) => line.lineId === suggestion.beforeLineId)
        insertIndex = beforeIdx === -1 ? 0 : beforeIdx
      } else {
        insertIndex = 0
      }
      next.splice(insertIndex, 0, insertLine)
      return next
    }
    default:
      return next
  }
}

export const dialogueToBulkPayload = (dialogue: DialogueLine[]) => dialogue.map((line) => ({
  lineId: line.lineId,
  text: line.line,
  host: line.speaker,
}))
